// 0.62.63.x — Custom TitleStrategy: replaces the hardcoded "FATIP" brand prefix
// in route title strings with the configured app name from BrandingService.
//
// Route titles in app.routes.ts follow the pattern "FATIP — <Page Name>".
// This strategy strips the leading brand prefix and prepends the effective
// app name at runtime, so that a tenant who sets appName = "MyPlatform" sees
// "MyPlatform — Plattform Management" in the browser tab instead of
// "FATIP — Plattform Management".
//
// Titles that do NOT start with the brand prefix are passed through unchanged
// (edge-case safety). Routes with no title fall back to just the app name.
//
// Registration: provided in app.config.ts as
//   { provide: TitleStrategy, useClass: AppTitleStrategy }

import { Injectable, Injector, inject } from '@angular/core';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { BrandingService } from './branding.service';

/** The literal brand prefix used in app.routes.ts route title strings. */
const BRAND_PREFIX = 'FATIP';

@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);

  // 0.72.3 — NG0200-Guard: BrandingService darf hier NICHT im Feld-Initializer
  // (= zur Konstruktionszeit) injiziert werden. Seit 0.71.3 injiziert
  // BrandingService selbst den Router (Favicon-Re-Assert bei NavigationEnd),
  // und die Router-Konstruktion instanziert diese TitleStrategy — eine
  // Konstruktions-Kante zurueck auf BrandingService schliesst den Zyklus
  // APP_INITIALIZER -> BrandingService -> Router -> TitleStrategy ->
  // BrandingService und bricht den kompletten App-Bootstrap ab (weisse
  // Seite auf Pilot, 0.71.3-0.72.2). Lazy-Aufloesung beim ersten
  // updateTitle() (laeuft erst nach dem Boot) bricht die Kante.
  private readonly injector = inject(Injector);
  private get branding(): BrandingService {
    return this.injector.get(BrandingService);
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const appName = this.branding.effectiveAppName();
    const routeTitle = this.buildTitle(snapshot);

    if (!routeTitle) {
      // No route title — show just the app name.
      this.title.setTitle(appName);
      return;
    }

    // Strip the hardcoded brand prefix (and the separator that follows it,
    // e.g. " — " or " · ") and replace with the effective app name.
    // Pattern: starts with BRAND_PREFIX followed by a non-word separator char.
    const prefixPattern = new RegExp(
      `^${BRAND_PREFIX}\\s*[\\u2014\\u2013\\-\\u00b7\\.·:]+\\s*`,
      'u'
    );

    const pageSegment = routeTitle.replace(prefixPattern, '').trim();

    if (pageSegment && pageSegment !== routeTitle) {
      // Prefix was present — reconstruct with the configured app name.
      this.title.setTitle(`${appName} — ${pageSegment}`);
    } else if (pageSegment) {
      // Title had no matching brand prefix — prepend app name to preserve
      // context for the user but still show the page segment.
      this.title.setTitle(`${appName} — ${pageSegment}`);
    } else {
      // Route title was only the brand prefix with no page segment.
      this.title.setTitle(appName);
    }
  }
}
